import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { getCurrentUser, User, markUserForDeletion, cancelDeletionRequest } from '../services/authService';
import { contactService } from '../services/contactService';
import Button from '../components/Button';
import TopBar from '../components/TopBar';
import ConfirmationModal from '../components/ConfirmationModal';
import { APP_NAME } from '../constants';

const Container = styled.div`
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (min-width: 768px) {
    padding: 0 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
`;

const Breadcrumbs = styled.div`
  display: flex;
  margin-bottom: 20px;
  font-size: 14px;
  color: #555;
`;

const BreadcrumbLink = styled.span`
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 8px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #333;
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 600px;
`;

const Description = styled.p`
  font-size: 16px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 30px;
  margin-top: 20px;
`;

const ButtonContainer = styled.div`
  gap: 20px;
  flex-wrap: wrap;
`;

const SuccessMessage = styled.div`
  background-color: #4caf50;
  color: white;
  padding: 15px 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  text-align: center;
  font-weight: 500;
`;

const ErrorMessage = styled.div`
  background-color: #f44336;
  color: white;
  padding: 15px 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  text-align: center;
  font-weight: 500;
`;

const LoadingPlaceholder = styled.div`
  text-align: center;
  padding: 40px;
  font-size: 16px;
  color: #666;
`;

const DeleteAccountPage: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const userData = await getCurrentUser();
        if (userData) {
          setUser(userData);
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  const handleNavigateToMyAccount = () => {
    navigate('/dashboard');
  };

  const handleDeleteRequest = () => {
    setShowConfirmationModal(true);
  };

  const handleConfirmDeletion = async () => {
    if (!user) return;

    setSubmitting(true);
    setError('');
    setShowConfirmationModal(false);

    try {
      // Mark user for deletion in Firestore
      await markUserForDeletion();

      // Send deletion request email
      await contactService.sendContactEmail({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phoneNumber || '',
        subject: 'Account Deletion Request',
        message: `Please delete my account and all associated data.

        User Details:
        - Name: ${user.firstName} ${user.lastName}
        - Email: ${user.email}
        - Phone: ${user.phoneNumber || 'Not provided'}
        - User ID: ${user.id}

        This is a formal request to delete my account and all personal data in accordance with data protection regulations.`
      });

      // Refresh user data to get updated markedForDeletion status
      const updatedUser = await getCurrentUser();
      setUser(updatedUser);
    } catch (error: any) {
      console.error('Error submitting delete request:', error);
      setError(error.message || 'Failed to submit delete request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelRequest = async () => {
    if (!user) return;

    setSubmitting(true);
    setError('');

    try {
      // Cancel deletion request in Firestore
      await cancelDeletionRequest();

      // Send cancellation email
      await contactService.sendContactEmail({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phoneNumber || '',
        subject: 'Account Deletion Cancellation Request',
        message: `Please cancel my account deletion request.

        User Details:
        - Name: ${user.firstName} ${user.lastName}
        - Email: ${user.email}
        - Phone: ${user.phoneNumber || 'Not provided'}
        - User ID: ${user.id}

        I would like to cancel my previous account deletion request and keep my account active.`
      });

      // Refresh user data to get updated markedForDeletion status
      const updatedUser = await getCurrentUser();
      setUser(updatedUser);
    } catch (error: any) {
      console.error('Error canceling delete request:', error);
      setError(error.message || 'Failed to cancel delete request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmationModal(false);
  };

  if (loading) {
    return (
      <Container>
        <LoadingPlaceholder>Loading...</LoadingPlaceholder>
      </Container>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Delete Account</title>
        <meta name="description" content="Request account deletion" />
      </Helmet>
      
      <Container>
        <ContentWrapper>
          <TopBar user={user} />
          
          <PageTitle>Delete Account</PageTitle>
          
          <Breadcrumbs>
            <BreadcrumbLink onClick={handleNavigateToMyAccount}>My Account</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <span>Delete Account</span>
          </Breadcrumbs>

          <FormContainer>
            {user?.markedForDeletion ? (
              // User is already marked for deletion
              <>
                <SuccessMessage>
                  Your account deletion request has been sent.
                </SuccessMessage>
                
                <Description>
                  Your account is scheduled for deletion. If you change your mind, you can cancel the request below.
                </Description>

                {error && <ErrorMessage>{error}</ErrorMessage>}

                <ButtonContainer>
                  <Button
                    text="Cancel Request"
                    onClick={handleCancelRequest}
                    loading={submitting}
                    outline={true}
                    bgColor="black"
                    textColor="black"
                    width="auto"
                    minWidth="160px"
                  />
                </ButtonContainer>
              </>
            ) : (
              // Default state - ready to submit deletion request
              <>
                <Description>
                  Tap the button below to submit a request to delete your account.
                </Description>

                {error && <ErrorMessage>{error}</ErrorMessage>}

                <ButtonContainer>
                  <Button
                    text="Delete My Account"
                    onClick={handleDeleteRequest}
                    loading={submitting}
                    bgColor="#f44336"
                    textColor="white"
                    width="auto"
                    minWidth="160px"
                  />
                </ButtonContainer>
              </>
            )}
          </FormContainer>
        </ContentWrapper>

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={showConfirmationModal}
          title="Delete Account"
          message="Are you sure you want to delete your account? This action will mark your account for deletion and cannot be undone easily."
          confirmText="Yes, Delete My Account"
          cancelText="Cancel"
          onConfirm={handleConfirmDeletion}
          onCancel={handleCancelConfirmation}
          isConfirming={submitting}
          confirmButtonVariant="danger"
        />
      </Container>
    </>
  );
};

export default DeleteAccountPage;